import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import {useTranslation} from 'react-i18next';

import {SingleSelect} from '../../../../../common/newSelect';
import {
    WAIT_UNIT_FACTORS,
    WaitUnit,
    formatWaitDuration,
    isWaitUnit,
    splitWaitDuration
} from '../../../utils/waitDuration';
import NodeEditorLayout from '../NodeEditorLayout';
import NodeNumberField from '../fields/NodeNumberField';
import {NodeEditorV2Props} from '../types';

/**
 * The Wait step. `duration` is always minutes and the only key the engine reads; `durationUnit` only remembers
 * the display unit. No duration is valid: the engine waits its default of one minute.
 */
const WaitEditorV2 = ({nodeConfig, setNodeConfig, readOnly}: NodeEditorV2Props) => {
    const {t} = useTranslation();

    const {value, unit: derivedUnit} = splitWaitDuration(nodeConfig);
    // With nothing typed yet the stored unit wins, so picking the unit before the number sticks.
    const unit: WaitUnit = value === '' && isWaitUnit(nodeConfig.durationUnit)
        ? nodeConfig.durationUnit
        : derivedUnit;

    const store = (nextValue: number | undefined, nextUnit: WaitUnit) => setNodeConfig({
        ...nodeConfig,
        duration: nextValue === undefined ? undefined : nextValue * WAIT_UNIT_FACTORS[nextUnit],
        durationUnit: nextUnit
    });

    const duration = formatWaitDuration(nodeConfig, t);

    const summary = duration
        ? t('Pauses the run for {{duration}}, then continues with the next step.', {duration})
        : t('No duration is set, so the run pauses for one minute and then continues.');

    return (
        <NodeEditorLayout state={duration ? 'configured' : 'caution'} summary={summary}>
            <Stack direction={'row'} gap={1.5} alignItems={'flex-start'}>
                <Box sx={{flex: 1, minWidth: 0}}>
                    <NodeNumberField
                        label={t('Wait for')}
                        value={value === '' ? undefined : value}
                        min={1}
                        onChange={next => store(
                            next !== undefined && next >= 1 ? Math.floor(next) : undefined,
                            unit
                        )}
                        disabled={readOnly}
                    />
                </Box>

                <Box sx={{width: 150, flexShrink: 0}}>
                    <SingleSelect
                        label={t('Unit')}
                        value={unit}
                        options={[
                            {value: 'minutes', label: t('Minutes')},
                            {value: 'hours', label: t('Hours')},
                            {value: 'days', label: t('Days')}
                        ]}
                        onChange={next => store(value === '' ? undefined : value, next as WaitUnit)}
                        disabled={readOnly}
                        disableClearable
                    />
                </Box>
            </Stack>
        </NodeEditorLayout>
    );
};

export default WaitEditorV2;
